import React, { useEffect, useState, useMemo } from "react";
import { Pie, Column } from "@ant-design/plots";
import { useWindowWidth } from "../../../../hooks/useWindowWidth";
import { getDonutHeight, BAR_PALETTE, DONUT_COLORS } from "../../../../utils/chartConfig";
import { formatWhole, formatPercentage } from "../../../../utils/numberFormat";
import "./TableGraf.scss";
import { EmptyChartMessage } from "../EmptyChartMessage";

export const waterGroupLabels = {
  Agu1: "Agua de red",
  Agu2: "Agua subterránea",
  Agu3: "Agua de lluvia",
  Agu4: "Agua superficial",
  Agu5: "Agua destilada",
  Agu6: "Agua reutilizada",
  Agu7: "Agua reciclada",
  Agu8: "Agua condesada de vapor",
};

// Los labels de arriba quedan fijos en español porque son la clave interna
// que matchea contra DONUT_COLORS.water/waterUsage (colores) — el texto que
// se muestra (pills, tooltip, leyenda del PDF) se traduce por separado con
// estos mapas. Mismo patrón que ENERGY_GROUP_LABEL_KEYS en
// EnergyKPIsDashboard/TableGraf.js.
export const WATER_SOURCE_LABEL_KEYS = {
  "Agua de red": "water_source_grid",
  "Agua subterránea": "groundwater",
  "Agua de lluvia": "rainwater",
  "Agua superficial": "surface_water",
  "Agua destilada": "distilled_water",
  "Agua reutilizada": "reused_water",
  "Agua reciclada": "recycled_water",
  "Agua condesada de vapor": "steam_condensed_water",
};

// Dona "Fuentes" — misma lógica que ya usaba el useEffect de TableGraf
// (getTotals + waterGroups) para armar dataFuente.
export const getWaterSourceTotals = (totales, year) => {
  const waterGroups = {
    Agu1: ["water_domestic_municipal_network", "municipal_network_water_production", "combined_municipal_network_water"],
    Agu2: ["domestic_groundwater", "groundwater_production", "groundwater_combined"],
    Agu3: ["rainwater"],
    Agu4: ["surface_water"],
    Agu5: ["reused_water"],
    Agu6: ["recycled_water"],
    Agu7: ["steam_condensed_water"],
  };
  const results = {};
  Object.entries(waterGroups).forEach(([groupKey, conceptKeys]) => {
    let total = 0;
    conceptKeys.forEach((concept) => {
      total += totales?.[concept]?.[year]?.total || 0;
    });
    results[groupKey] = { total };
  });
  return results;
};

// Barra "Consumo" — misma lógica que ya usaba el useMemo de WaterBarChart.
export const buildWaterBarChartData = (data) => {
  if (!data?.total_water_consumption_kpis) return [];
  return Object.entries(data.total_water_consumption_kpis)
    .filter(([_, { total }]) => total > 0)
    .map(([year, { total }]) => ({ year, value: total }))
    .sort((a, b) => a.year - b.year).slice(-5);
};

const waterUsageLabels = {
  total_water_production_kpis: "Producción",
  total_domestic_water_kpis: "Doméstica",
  combined_total_water_kpis: "Combinada",
};

export const WATER_USAGE_LABEL_KEYS = {
  "Producción": "production",
  "Doméstica": "usage_domestic",
  "Combinada": "usage_combined",
};

// Dona "Usos" — misma lógica que ya usaba el useMemo de WaterUsosDonut.
export const buildWaterUsageData = (totales, year) => {
  const fields = ["total_water_production_kpis", "total_domestic_water_kpis", "combined_total_water_kpis"];
  return fields
    .map((key) => ({
      type: waterUsageLabels[key],
      value: totales?.[key]?.[year]?.total || 0,
    }))
    .filter((item) => item.value > 0);
};

const TableGraf = ({ filters, totales, t }) => {
  const { year } = filters || {};
  const [dataFuente, setDataFuente] = useState({});
  const windowWidth = useWindowWidth();

  const waterColorMapDonut = DONUT_COLORS.water;

  const barPalette = BAR_PALETTE;

  const responsiveConfig = useMemo(() => {
    const isMobile = windowWidth <= 768;
    const isSmallMobile = windowWidth <= 480;
    const isNotebook = windowWidth >= 1024 && windowWidth <= 1500;
    return {
      isMobile,
      isSmallMobile,
      chartHeight: {
        donut: getDonutHeight(isNotebook, isMobile),
        bar: isSmallMobile ? 250 : isMobile ? 280 : 300,
      },
    };
  }, [windowWidth]);

  useEffect(() => {
    if (!totales || !year) return;
    setDataFuente(getWaterSourceTotals(totales, year));
  }, [totales, year]);

  // --- COMPONENTE 1: DONUT FUENTES CON PILLS ---
  const WaterFuentesDonut = ({ data, t }) => {
    const [hiddenTypes, setHiddenTypes] = useState([]);

    const allData = useMemo(() =>
      Object.entries(data)
        .filter(([_, { total }]) => total > 0)
        .map(([key, { total }]) => ({
          type: waterGroupLabels[key],
          value: total,
        })), [data]);

    const chartData = useMemo(
      () => allData.filter((d) => !hiddenTypes.includes(d.type)),
      [allData, hiddenTypes]
    );

    const totalVisible = chartData.reduce((sum, d) => sum + d.value, 0);
    const totalAll = allData.reduce((sum, d) => sum + d.value, 0);

    const config = {
      data: chartData,
      height: responsiveConfig.chartHeight.donut,
      angleField: "value",
      colorField: "type",
      scale: {
        color: {
          domain: Object.keys(waterColorMapDonut),
          range: Object.values(waterColorMapDonut),
        },
      },
      radius: 1,
      innerRadius: 0.6,
      legend: false,
      statistic: {
        title: false,
        content: {
          style: { fontSize: "16px", fontWeight: "600" },
          content: `${t("total")}\n${formatWhole(totalVisible)}`,
        },
      },
      tooltip: {
        items: [
          (datum) => ({
            name: t(WATER_SOURCE_LABEL_KEYS[datum.type] || datum.type),
            value: `${formatWhole(datum.value)} M3 (${totalAll > 0 ? formatPercentage((datum.value / totalAll) * 100) : formatPercentage(0)})`,
          }),
        ],
      },
    };

    if (allData.length === 0) return <EmptyChartMessage message={t("no_data")} />;

    return (
      <>
        <div className="chartWrapper"><Pie {...config} /></div>
        <div className="pillsGrid">
          {allData.map((item) => {
            const isHidden = hiddenTypes.includes(item.type);
            const color = waterColorMapDonut[item.type];
            return (
              <div
                key={item.type}
                onClick={() => setHiddenTypes(prev => isHidden ? prev.filter(y => y !== item.type) : [...prev, item.type])}
                className="yearPill"
                style={{
                  backgroundColor: isHidden ? "#E5E7EB" : color,
                  color: isHidden ? "#9CA3AF" : "#FFF",
                  opacity: isHidden ? 0.7 : 1,
                  cursor: "pointer"
                }}
              >
                {t(WATER_SOURCE_LABEL_KEYS[item.type] || item.type)}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  // --- COMPONENTE 2: BARRAS CONSUMO ANUAL CON PILLS ---
  const WaterBarChart = ({ data }) => {
    const rawData = useMemo(() => buildWaterBarChartData(data), [data]);

    const allYears = rawData.map(d => d.year);
    const [activeYears, setActiveYears] = useState(allYears);

    const yearColorMap = useMemo(() => {
      const map = {};
      allYears.forEach((y, i) => map[y] = barPalette[i] || "#ccc");
      return map;
    }, [allYears]);

    const chartData = rawData.filter(d => activeYears.includes(d.year));

    const config = {
      data: chartData,
      height: responsiveConfig.chartHeight.bar,
      xField: "year",
      yField: "value",
      colorField: "year",
      scale: { color: { domain: Object.keys(yearColorMap), range: Object.values(yearColorMap) } },
      legend: false,
      axis: {
        y: { labelFormatter: (v) => formatWhole(v) },
      },
      tooltip: {
        items: [
          (datum) => ({
            name: t("water_consumption") || "Consumo",
            value: `${formatWhole(datum.value)} M3`,
          }),
        ],
      },
    };

    return (
      <>
        <div className="chartWrapper"><Column {...config} /></div>
        <div className="pillsRow">
          {allYears.map(year => (
            <div
              key={year}
              className="yearPill"
              onClick={() => setActiveYears(prev => prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year])}
              style={{ backgroundColor: activeYears.includes(year) ? yearColorMap[year] : "#ccc", cursor: 'pointer' }}
            >
              {year}
            </div>
          ))}
        </div>
      </>
    );
  };

  // --- COMPONENTE 3: DONUT USOS CON PILLS ---
  const WaterUsosDonut = ({ totales, year, t }) => {
    const [hiddenUsos, setHiddenUsos] = useState([]);

    const usageColorMap = DONUT_COLORS.waterUsage;

    const allData = useMemo(() => buildWaterUsageData(totales, year), [totales, year]);

    const chartData = useMemo(
      () => allData.filter((d) => !hiddenUsos.includes(d.type)),
      [allData, hiddenUsos]
    );

    const totalVisible = chartData.reduce((sum, d) => sum + d.value, 0);
    const totalAll = allData.reduce((sum, d) => sum + d.value, 0);

    const config = {
      data: chartData,
      height: responsiveConfig.chartHeight.donut,
      angleField: "value",
      colorField: "type",
      scale: {
        color: {
          domain: Object.keys(usageColorMap),
          range: Object.values(usageColorMap),
        },
      },
      radius: 1,
      innerRadius: 0.6,
      legend: false,
      statistic: {
        title: false,
        content: {
          style: { fontSize: "16px", fontWeight: "600" },
          content: `${t("total")}\n${formatWhole(totalVisible)}`,
        },
      },
      tooltip: {
        items: [
          (datum) => ({
            name: t(WATER_USAGE_LABEL_KEYS[datum.type] || datum.type),
            value: `${formatWhole(datum.value)} M3 (${totalAll > 0 ? formatPercentage((datum.value / totalAll) * 100) : formatPercentage(0)})`,
          }),
        ],
      },
    };

    if (allData.length === 0) return <EmptyChartMessage message={t("no_data")} />;

    return (
      <>
        <div className="chartWrapper"><Pie {...config} /></div>
        <div className="pillsGrid">
          {allData.map((item) => {
            const isHidden = hiddenUsos.includes(item.type);
            const color = usageColorMap[item.type] || "#D9D9D9";
            return (
              <div
                key={item.type}
                onClick={() => setHiddenUsos(prev => isHidden ? prev.filter(y => y !== item.type) : [...prev, item.type])}
                className="yearPill"
                style={{
                  backgroundColor: isHidden ? "#E5E7EB" : color,
                  color: isHidden ? "#9CA3AF" : "#FFF",
                  opacity: isHidden ? 0.7 : 1,
                  cursor: "pointer"
                }}
              >
                {t(WATER_USAGE_LABEL_KEYS[item.type] || item.type)}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className="waterDashboardGrid">
      <div className="chartCard">
        <h3 className="cardTitle">{t("water_sources")}</h3>
        <WaterFuentesDonut data={dataFuente} t={t} />
      </div>
      <div className="chartCard">
        <h3 className="cardTitle">{t("water_consumption")}</h3>
        <WaterBarChart data={totales} />
      </div>
      <div className="chartCard">
        <h3 className="cardTitle">{t("usage_type")}</h3>
        <WaterUsosDonut totales={totales} year={year} t={t} />
      </div>
    </div>
  );
};

export default TableGraf;
